/** Summary P&L view — sits inside the Flash Report under the Report/P&L toggle.
 *
 *  v2 (Phase 4):
 *  - Period column = the active date-picker range (already true).
 *  - Optional comparator columns: Budget (stub), STLY, YTD. Each renders £ and
 *    £/% delta vs primary.
 *  - Click any £ on the primary table to open a drill-down modal showing the
 *    underlying invoice line items, with group-by (supplier/account/day) and
 *    sortable columns.
 *  - Admin-only pencil icon next to the section heading to toggle which
 *    comparators are enabled by default for this client.
 */
import { useEffect, useMemo, useState } from "react";
import { useAdmin } from "@/lib/auth";
import { api, fmtGBP2, fmtDateUK } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { PencilSimple, Check, CaretUp, CaretDown, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import FlashDisclaimer from "@/components/FlashDisclaimer";
import { xeroInvoiceUrl } from "@/lib/xeroLinks";
import { PdfErrorState } from "@/components/PdfErrorState";

/** Small pill button that opens an invoice in Xero in a new tab.
 *  Stops event propagation so clicking it inside a clickable table row
 *  doesn't also trigger the invoice-detail modal. */
function XeroOpenButton({ invoice, size = "xs" }) {
  const url = xeroInvoiceUrl(invoice);
  if (!url) return null;
  const cls = size === "sm"
    ? "text-[11px] px-2 py-1 rounded"
    : "text-[11px] px-1.5 py-0.5 rounded";
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      data-testid={`open-in-xero-${invoice.invoice_id}`}
      className={`${cls} inline-flex items-center gap-1 border border-[#E2E8F0] text-[#475569] hover:text-[#0F172A] hover:border-[#0F172A] font-mono uppercase tracking-[0.15em] transition-colors`}
      title="Open this invoice in Xero"
    >
      <ArrowSquareOut size={size === "sm" ? 11 : 9} weight="bold" /> Xero
    </a>
  );
}

const BUCKET_LABELS = {
  food_sales: "Food Sales",
  bev_sales:  "Beverage Sales",
  food_cos:   "Food Cost of Sales",
  bev_cos:    "Beverage Cost of Sales",
  other:      "Other Expenses",
};

export default function PnLSummary({ token, params }) {
  const adminMe = useAdmin();
  const [pnl, setPnl] = useState(null);
  const [error, setError] = useState("");
  const [drill, setDrill] = useState(null);   // { bucket, label }
  const [rootId, setRootId] = useState(null);

  // Fixed comparator set: always fetch YTD alongside the primary period.
  // STLY / Budget columns were retired per the Feb 2026 P&L spec update.
  useEffect(() => {
    if (!params) return;
    const qp = { comparators: "ytd" };
    if (params.granularity) qp.granularity = params.granularity;
    if (params.date_from) qp.date_from = params.date_from;
    if (params.date_to) qp.date_to = params.date_to;
    if (params.site_id && params.site_id !== "consolidated") qp.site_id = params.site_id;
    setError("");
    api.get(`/public/clients/${token}/pnl`, { params: qp })
      .then((r) => {
        setPnl(r.data);
        setRootId(r.data?.root_client_id);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load P&L"));
  }, [params, token]);

  if (error) return <div className="text-sm text-rose-700 py-10 text-center" data-testid="pnl-error">{error}</div>;
  if (!pnl) return <div className="text-sm text-[#94A3B8] py-10 text-center">Loading P&L...</div>;

  const { summary } = pnl;
  const isAdmin = !!adminMe;
  const _adminHint = isAdmin && rootId;   // retained for future admin affordances on this view

  return (
    <div className="space-y-8" data-testid="pnl-summary">
      <div className="bg-white border border-[#E2E8F0] rounded-md card-fade overflow-hidden">
        <div className="p-6 border-b border-[#E2E8F0] flex items-baseline justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div>
              <p className="uppercase text-[11px] tracking-[0.22em] text-[#475569] font-medium">Summary Profit &amp; Loss</p>
              <h2 className="font-display text-[18px] sm:text-[20px] font-semibold tracking-tight mt-1">Operating margin breakdown</h2>
            </div>
          </div>
          <p className="text-[11px] font-mono text-[#94A3B8] uppercase tracking-[0.18em]" data-testid="pnl-vat-basis">
            {params?.vat === "gross" ? "Inc VAT" : "Ex VAT"} · {fmtDateUK(pnl.date_from)} → {fmtDateUK(pnl.date_to)}
          </p>
        </div>

        <Table data-testid="pnl-table">
          <TableHeader>
            <TableRow className="border-[#E2E8F0]">
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] px-4 sm:px-6 h-10">Line</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10">£ Period</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10 max-sm:pr-4 whitespace-nowrap">% of Sales</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10 hidden sm:table-cell" data-testid="pnl-col-ytd">YTD</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right h-10 pr-6 hidden sm:table-cell" data-testid="pnl-col-ytd-pct">% of Sales</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <PnlGroup label="REVENUE" cols={5} />
            <PnlRow
              label="Food Sales" bucket="food_sales" primary={summary.food_sales}
              ytd={pnl.ytd?.food_sales} pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales}
              onDrill={() => setDrill({ bucket: "food_sales", label: BUCKET_LABELS.food_sales })}
            />
            <PnlRow
              label="Beverage Sales" bucket="bev_sales" primary={summary.bev_sales}
              ytd={pnl.ytd?.bev_sales} pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales}
              onDrill={() => setDrill({ bucket: "bev_sales", label: BUCKET_LABELS.bev_sales })}
            />
            <PnlRow label="Total Revenue" primary={summary.total_sales}
              ytd={pnl.ytd?.total_sales} pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales}
              totalRow />

            <PnlGroup label="COST OF SALES" cols={5} />
            <PnlRow
              label="Food Cost of Sales" bucket="food_cos" primary={-summary.food_cos}
              ytd={pnl.ytd?.food_cos != null ? -pnl.ytd.food_cos : undefined}
              pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales} negative
              onDrill={() => setDrill({ bucket: "food_cos", label: BUCKET_LABELS.food_cos })}
            />
            <PnlRow
              label="Beverage Cost of Sales" bucket="bev_cos" primary={-summary.bev_cos}
              ytd={pnl.ytd?.bev_cos != null ? -pnl.ytd.bev_cos : undefined}
              pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales} negative
              onDrill={() => setDrill({ bucket: "bev_cos", label: BUCKET_LABELS.bev_cos })}
            />
            <PnlRow label="Total Cost of Sales" primary={-summary.total_cos}
              ytd={pnl.ytd?.total_cos != null ? -pnl.ytd.total_cos : undefined}
              pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales} negative totalRow />

            <PnlGroup label="GROSS PROFIT" cols={5} />
            <PnlRow label="Gross Profit" primary={summary.gross_profit}
              ytd={pnl.ytd?.gross_profit}
              pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales} accent="#059669" totalRow />

            {pnl.rows.other.length > 0 && (
              <>
                <PnlGroup label="OTHER EXPENSES (Operating)" cols={5} />
                <PnlRow label="Total Other Expenses" primary={-summary.total_other_expenses}
                  ytd={pnl.ytd?.total_other_expenses != null ? -pnl.ytd.total_other_expenses : undefined}
                  pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales} negative totalRow
                  onDrill={() => setDrill({ bucket: "other", label: BUCKET_LABELS.other })}
                />
              </>
            )}

            <PnlGroup label="NET OPERATING PROFIT" cols={5} />
            <PnlRow label="Net Operating Profit" primary={summary.net_profit}
              ytd={pnl.ytd?.net_profit}
              pctBase={summary.total_sales} ytdPctBase={pnl.ytd?.total_sales} accent="#059669" totalRow />
          </TableBody>
        </Table>
      </div>

      <div className="hidden print:block">
        <FlashDisclaimer />
      </div>

      <PnlDrillModal
        token={token}
        params={params}
        open={!!drill}
        bucket={drill?.bucket}
        title={drill?.label}
        onClose={() => setDrill(null)}
        dateFrom={pnl.date_from}
        dateTo={pnl.date_to}
        siteId={params?.site_id}
      />
    </div>
  );
}

function PnlGroup({ label, cols = 5 }) {
  return (
    <TableRow className="border-[#E2E8F0]">
      <TableCell colSpan={cols} className="px-4 sm:px-6 py-2 text-[11px] uppercase tracking-[0.22em] font-medium text-[#475569] bg-[#EEEEEE]">{label}</TableCell>
    </TableRow>
  );
}

function PnlRow({ label, bucket, primary, ytd, pctBase, ytdPctBase, negative, totalRow, accent, onDrill }) {
  const colour = accent || (negative ? "#991B1B" : "#0F172A");
  const pct = pctBase ? ((primary || 0) / pctBase) * 100 : 0;
  const ytdPct = ytdPctBase ? ((ytd || 0) / ytdPctBase) * 100 : null;
  const rowClass = totalRow ? "border-[#E2E8F0] bg-[#F5F5F5]" : "border-[#EEEEEE]";
  const fontWeight = totalRow ? "font-medium" : "";
  const clickable = !!onDrill && primary != null && Math.abs(primary) > 0;

  return (
    <TableRow className={rowClass}>
      <TableCell className={`text-xs px-4 sm:px-6 py-2.5 text-[#0F172A] ${fontWeight}`}>{label}</TableCell>
      <TableCell
        className={`text-right font-mono-num text-xs py-2.5 whitespace-nowrap ${fontWeight} ${clickable ? "cursor-pointer hover:underline" : ""}`}
        style={{ color: colour }}
        onClick={clickable ? onDrill : undefined}
        data-testid={bucket ? `pnl-cell-${bucket}` : undefined}
      >
        {fmtGBP2(primary)}
      </TableCell>
      <TableCell className={`text-right font-mono-num text-xs py-2.5 max-sm:pr-4 ${fontWeight} text-[#94A3B8]`}>{(pct ?? 0).toFixed(1)}%</TableCell>
      <TableCell className={`text-right font-mono-num text-xs py-2.5 hidden sm:table-cell whitespace-nowrap ${fontWeight}`} style={{ color: ytd != null ? colour : "#94A3B8" }}>
        {ytd != null ? fmtGBP2(ytd) : "—"}
      </TableCell>
      <TableCell className={`text-right font-mono-num text-xs pr-6 py-2.5 hidden sm:table-cell ${fontWeight} text-[#94A3B8]`}>
        {ytdPct != null ? `${ytdPct.toFixed(1)}%` : "—"}
      </TableCell>
    </TableRow>
  );
}

/* ----------------------------- Editor popover ----------------------------- */
function PnlEditor({ clientId, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  const openChange = (next) => {
    setOpen(next);
    if (next) setDraft(initial);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/report-config`, {
        summary_pnl: { comparators: { ...draft } },
      });
      toast.success("P&L settings saved");
      setOpen(false);
      onSaved?.(draft);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={openChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="edit-pnl"
          className="inline-flex items-center justify-center w-6 h-6 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#EEEEEE] rounded-md transition-colors"
          aria-label="Edit P&L"
        >
          <PencilSimple size={14} weight="regular" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4 rounded-md border-[#E2E8F0]" data-testid="edit-pnl-popover">
        <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-3">Edit · P&L Comparators</p>
        <div className="divide-y divide-[#EEEEEE] -my-1">
          <Toggle label="STLY column" sub="Same time last year — £ and £/% variance."
            value={!!draft?.stly} onChange={(b) => setDraft({ ...draft, stly: b })} />
          <Toggle label="YTD column" sub="Year-to-date totals — £ and £/% variance."
            value={!!draft?.ytd} onChange={(b) => setDraft({ ...draft, ytd: b })} />
          <Toggle label="Budget column" sub="Budget from Xero — requires Budgets API wiring."
            value={!!draft?.budget} onChange={(b) => setDraft({ ...draft, budget: b })}
            disabled badge="coming soon" />
        </div>
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[#EEEEEE]">
          <Button type="button" variant="ghost" size="sm" className="h-8 text-xs"
            onClick={() => setOpen(false)} data-testid="edit-pnl-cancel">Cancel</Button>
          <Button type="button" size="sm" disabled={saving} onClick={save} data-testid="edit-pnl-save"
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md h-8 text-xs">
            <Check size={11} weight="bold" className="mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Toggle({ label, sub, value, onChange, disabled, badge }) {
  return (
    <div className={`flex items-start justify-between gap-3 py-2 ${disabled ? "opacity-50" : ""}`}>
      <div className="min-w-0">
        <p className="text-xs text-[#0F172A] font-medium flex items-center gap-1.5">
          {label}
          {badge && (
            <span className="text-[11px] uppercase tracking-[0.18em] font-mono bg-[#EEEEEE] text-[#475569] px-1.5 py-0.5 rounded">
              {badge}
            </span>
          )}
        </p>
        {sub && <p className="text-[11px] text-[#94A3B8] mt-0.5 leading-snug">{sub}</p>}
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

/* ----------------------------- Drill modal ------------------------------ */
const GROUP_OPTIONS = [
  { v: "none", l: "No grouping" },
  { v: "supplier", l: "By supplier" },
  { v: "account", l: "By account" },
  { v: "day", l: "By day" },
];

function PnlDrillModal({ token, open, bucket, title, onClose, dateFrom, dateTo, siteId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState("none");
  const [sortKey, setSortKey] = useState("amount");
  const [sortDir, setSortDir] = useState("desc");
  // Nested invoice drill-in: which invoice_id is the user inspecting?
  const [invDrill, setInvDrill] = useState(null);

  useEffect(() => {
    if (!open || !bucket) { setData(null); return; }
    setLoading(true);
    const qp = { bucket, date_from: dateFrom, date_to: dateTo };
    if (siteId && siteId !== "consolidated") qp.site_id = siteId;
    api.get(`/public/clients/${token}/pnl/lines`, { params: qp })
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [open, bucket, token, dateFrom, dateTo, siteId]);

  const grouped = useMemo(() => {
    if (!data) return [];
    if (group === "none") {
      const dir = sortDir === "asc" ? 1 : -1;
      return [...data.rows].sort((a, b) => {
        const va = a[sortKey]; const vb = b[sortKey];
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
        return String(va || "").localeCompare(String(vb || "")) * dir;
      });
    }
    const keyOf = (r) => group === "supplier" ? r.supplier : group === "account" ? r.account_name : r.invoice_date;
    const map = new Map();
    for (const r of data.rows) {
      const k = keyOf(r) || "—";
      const prev = map.get(k) || { key: k, count: 0, amount: 0, rows: [] };
      prev.count += 1;
      prev.amount += r.amount;
      prev.rows.push(r);
      map.set(k, prev);
    }
    const out = Array.from(map.values()).sort((a, b) =>
      group === "day" ? String(a.key).localeCompare(String(b.key)) : b.amount - a.amount);
    return out;
  }, [data, group, sortKey, sortDir]);

  const onSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-4xl" data-testid="pnl-drill-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {title} · {fmtDateUK(dateFrom)} → {fmtDateUK(dateTo)}
          </DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-[#94A3B8] py-6 text-center">Loading line items...</p>}
        {!loading && data && (
          <>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-1">
                <span className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#475569] mr-2">Group by</span>
                {GROUP_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setGroup(o.v)}
                    data-testid={`pnl-drill-group-${o.v}`}
                    className={`text-[11px] px-2.5 py-1 rounded ${group === o.v ? "bg-[#0F172A] text-white" : "text-[#0F172A] hover:bg-[#EEEEEE]"}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              <p className="text-xs font-mono text-[#475569]">
                {data.rows.length} line items · Total <span className="font-medium text-[#0F172A]">{fmtGBP2(data.total)}</span>
              </p>
            </div>

            {group !== "none" ? (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="pnl-drill-grouped">
                {grouped.map((g) => (
                  <details key={g.key} className="border border-[#E2E8F0] rounded-md">
                    <summary className="px-4 py-2.5 cursor-pointer flex items-center justify-between text-xs">
                      <span className="font-medium">{/^\d{4}-\d{2}-\d{2}$/.test(g.key) ? fmtDateUK(g.key) : g.key}</span>
                      <span className="font-mono-num">{g.count} · {fmtGBP2(g.amount)}</span>
                    </summary>
                    <Table>
                      <TableBody>
                        {g.rows.map((r, i) => (
                          <TableRow
                            key={`${r.invoice_id}-${i}`}
                            className="border-[#EEEEEE] cursor-pointer hover:bg-[#F5F5F5]"
                            onClick={() => setInvDrill(r.invoice_id)}
                            data-testid={`pnl-drill-row-${r.invoice_id}`}
                          >
                            <TableCell className="text-xs px-4 py-2 text-[#475569]">{fmtDateUK(r.invoice_date)}</TableCell>
                            <TableCell className="text-xs px-4 py-2">{r.supplier}</TableCell>
                            <TableCell className="text-xs px-4 py-2 truncate max-w-xs">{r.description}</TableCell>
                            <TableCell className="text-xs px-4 py-2 font-mono-num text-right">{fmtGBP2(r.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </details>
                ))}
              </div>
            ) : (
              <div className="max-h-[60vh] overflow-y-auto">
                <Table data-testid="pnl-drill-table">
                  <TableHeader>
                    <TableRow className="border-[#E2E8F0]">
                      <SortHead k="invoice_date" label="Date" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHead k="supplier" label="Supplier" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <SortHead k="account_name" label="Account" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                      <TableHead className="text-[11px] uppercase tracking-wider text-[#475569]">Description</TableHead>
                      <SortHead k="amount" label="£" sortKey={sortKey} sortDir={sortDir} onSort={onSort} align="right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grouped.map((r, i) => (
                      <TableRow
                        key={`${r.invoice_id}-${i}`}
                        className="border-[#EEEEEE] cursor-pointer hover:bg-[#F5F5F5]"
                        onClick={() => setInvDrill(r.invoice_id)}
                        data-testid={`pnl-drill-row-${r.invoice_id}`}
                      >
                        <TableCell className="text-xs px-3 py-2 font-mono-num text-[#475569]">{fmtDateUK(r.invoice_date)}</TableCell>
                        <TableCell className="text-xs px-3 py-2">{r.supplier}</TableCell>
                        <TableCell className="text-xs px-3 py-2 text-[#475569]">{r.account_name}</TableCell>
                        <TableCell className="text-xs px-3 py-2 max-w-xs truncate" title={r.description}>{r.description}</TableCell>
                        <TableCell className="text-xs px-3 py-2 font-mono-num text-right">{fmtGBP2(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
        <InvoiceDetailModal
          token={token}
          open={!!invDrill}
          invoiceId={invDrill}
          siteId={siteId}
          onClose={() => setInvDrill(null)}
        />
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------- Invoice detail modal ----------------------------- */
function InvoiceDetailModal({ token, open, invoiceId, siteId, onClose }) {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // The invoice-detail modal has two tabs: "Line items" (structured drill)
  // and "PDF" (Xero's rendered PDF served inline via /invoice/:id/pdf).
  const [tab, setTab] = useState("lines");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    if (!open || !invoiceId) { setInv(null); setError(""); setTab("lines"); setPdfUrl(null); setPdfError(""); return; }
    setLoading(true);
    const qp = {};
    if (siteId && siteId !== "consolidated") qp.site_id = siteId;
    api.get(`/public/clients/${token}/invoice/${invoiceId}`, { params: qp })
      .then((r) => { setInv(r.data); setError(""); })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load invoice"))
      .finally(() => setLoading(false));
  }, [open, invoiceId, token, siteId]);

  // Fetch the PDF lazily — only when the user clicks the "PDF" tab, so the
  // Xero /Invoices/{id} PDF request isn't triggered for every drill-in.
  useEffect(() => {
    if (tab !== "pdf" || !open || !invoiceId || pdfUrl || pdfLoading) return;
    setPdfLoading(true);
    setPdfError("");
    const qp = new URLSearchParams();
    if (siteId && siteId !== "consolidated") qp.set("site_id", siteId);
    api.get(`/public/clients/${token}/invoice/${invoiceId}/pdf?${qp.toString()}`, {
      responseType: "blob",
    })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        setPdfUrl(url);
      })
      .catch(async (e) => {
        let detail = "";
        try {
          const raw = await e?.response?.data?.text?.();
          detail = raw ? JSON.parse(raw).detail : "";
        } catch (parseErr) {
          console.error("PDF error body unreadable", parseErr);
        }
        setPdfError(detail || e?.response?.data?.detail || "load_failed");
      })
      .finally(() => setPdfLoading(false));
  }, [tab, open, invoiceId, token, siteId, pdfUrl, pdfLoading]);

  // Revoke the blob URL when the modal closes so we don't leak memory.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const linesTotal = inv?.line_items?.reduce((s, l) => s + (Number(l.line_amount) || 0), 0) ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-4xl" data-testid="invoice-detail-modal" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="font-display text-2xl tracking-tight truncate">
                {inv?.contact_name || "Invoice"} {inv?.invoice_number ? `· ${inv.invoice_number}` : ""}
              </DialogTitle>
              {inv && (
                <p className="text-xs text-[#475569] font-mono mt-1">
                  {fmtDateUK(inv.invoice_date)} · {inv.type === "ACCPAY" ? "Bill" : inv.type === "ACCREC" ? "Sales Invoice" : inv.type} · {inv.status} · Total {fmtGBP2(inv.total)}
                </p>
              )}
            </div>
          </div>
          {/* Tab switch: Line items ↔ Invoice Preview */}
          {inv && (
            <div className="flex items-center bg-[#EEEEEE] border border-[#E2E8F0] rounded-md p-0.5 w-fit mt-3" data-testid="invoice-detail-tabs">
              <button
                onClick={() => setTab("lines")}
                data-testid="invoice-detail-tab-lines"
                className={`text-[11px] px-3 py-1.5 rounded transition-colors ${tab === "lines" ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-white"}`}
              >
                Line items
              </button>
              <button
                onClick={() => setTab("pdf")}
                data-testid="invoice-detail-tab-pdf"
                className={`text-[11px] px-3 py-1.5 rounded transition-colors ${tab === "pdf" ? "bg-[#0F172A] text-white" : "text-[#475569] hover:bg-white"}`}
              >
                Invoice Preview
              </button>
            </div>
          )}
        </DialogHeader>
        {loading && <p className="text-sm text-[#94A3B8] py-6 text-center">Loading invoice...</p>}
        {error && <p className="text-sm text-rose-700 py-6 text-center" data-testid="invoice-detail-error">{error}</p>}
        {inv && tab === "lines" && (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table data-testid="invoice-detail-table">
              <TableHeader>
                <TableRow className="border-[#E2E8F0]">
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569]">Account</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569]">Description</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right">Qty</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right">Unit £</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-[#475569] text-right">Line £</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inv.line_items.map((l, i) => {
                  const tone = l.classification === "DRY" ? "text-orange-800" : l.classification === "WET" ? "text-emerald-800" : l.classification === "INTERCOMPANY" ? "text-[#94A3B8]" : "text-[#475569]";
                  return (
                    <TableRow key={l.line_item_id || `${l.account_code}-${l.description}-${i}`} className="border-[#EEEEEE]">
                      <TableCell className="text-xs px-3 py-2">
                        <span className={`text-[11px] uppercase tracking-[0.18em] font-mono mr-2 ${tone}`}>{l.classification}</span>
                        <span className="text-[#0F172A]">{l.account_name}</span>
                      </TableCell>
                      <TableCell className="text-xs px-3 py-2 max-w-md truncate" title={l.description}>{l.description}</TableCell>
                      <TableCell className="text-xs px-3 py-2 font-mono-num text-right text-[#475569]">{(Number(l.quantity) || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs px-3 py-2 font-mono-num text-right text-[#475569]">{fmtGBP2(l.unit_amount)}</TableCell>
                      <TableCell className="text-xs px-3 py-2 font-mono-num text-right">{fmtGBP2(l.line_amount)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="border-[#E2E8F0] bg-[#F5F5F5]">
                  <TableCell colSpan={4} className="text-xs px-3 py-2 font-medium text-right">Line items total</TableCell>
                  <TableCell className="text-xs px-3 py-2 font-mono-num font-medium text-right">{fmtGBP2(linesTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
        {inv && tab === "pdf" && (
          <div className="border border-[#E2E8F0] rounded-md overflow-hidden bg-[#F5F5F5]" data-testid="invoice-pdf-viewer">
            {pdfLoading && <p className="text-sm text-[#94A3B8] py-16 text-center">Fetching supplier PDF from Xero...</p>}
            {pdfError && !pdfLoading && (
              <PdfErrorState error={pdfError} testid="invoice-pdf" />
            )}
            {pdfUrl && !pdfError && (
              <iframe
                src={pdfUrl}
                title="Supplier invoice PDF"
                className="w-full"
                style={{ height: "70vh" }}
                data-testid="invoice-pdf-iframe"
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SortHead({ k, label, sortKey, sortDir, onSort, align }) {
  const active = sortKey === k;
  return (
    <TableHead
      className={`text-[11px] uppercase tracking-wider text-[#475569] cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      onClick={() => onSort(k)}
      data-testid={`pnl-drill-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (sortDir === "asc" ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />)}
      </span>
    </TableHead>
  );
}

function BreakdownCard() {
  // Removed in Phase 1 — kept as a no-op so any external references don't break.
  return null;
}
